import { defineComponent as _defineComponent } from 'vue'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const __default__ = require('@nuxtjs/composition-api').defineComponent({
  name: 'TeachingResourceCollection',
  layout: 'default',
  head: {},
  jsonld() {
    // @ts-ignore
    return this.$jsonSchemaPagewithFaqs(this.faqContent, this.title, this.$route.path);
  },
});

import {
  computed,
  ref,
  useAsync,
  useContext,
  useMeta,
  useRoute,
  useRouter,
  watch,
} from '@nuxtjs/composition-api';
import { marked } from 'marked';
import CollectionHero from './CollectionHero.vue';
import PinnedResources from './PinnedResources.vue';
import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import FaqSection from '~/components/collection/FaqSection.vue';
import SignupCta from '~/components/collection/SignupCta.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { shortCategoryTitle, breadcrumbs, BreadCrumb } from '~/utils/category';
import { useUser } from '~/composables/useUser';
import { isLocale } from '~/types/locale';
import { useFilteredLandingPage } from '~/composables/useFilteredLandingPage';
import { RootCategory, PublicV1FiltersRequest } from '~/types/api';
import { Resource, CountryLink } from '~/types/resource';
import { Collection } from '~/types/strapi/collection';
import { useStrapi } from '~/composables/useStrapi';
import { getMetaFromStrapiSEO } from '~/utils/seo';
import PinnedBlogs from '~/components/collection/PinnedBlogs.vue';
import { useLocale } from '~/composables/useLocale';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const $router = useRouter();
const $route = useRoute();
const { i18n, error, $config, $hrefLangsResource, $handleRedirect } = useContext();
const { slug, yearLevels, resourceTypes } = useFilteredLandingPage($route.value.params);
const resourceListEl = ref<HTMLElement | null>(null);

const blogs = computed<Resource[]>(() => collection.value?.attributes.pinned_blogs?.items ?? []);
const resources = computed<Resource[]>(
  () => collection.value?.attributes.pinned_resources?.items ?? []
);
const locale = useLocale();
const root = computed<RootCategory>(() => `resource-collection-${locale.value}`);
const localizations = computed<CountryLink[] | undefined>(() => {
  return collection.value?.attributes.localizations.data
    ? collection.value?.attributes.localizations.data.map(item => {
        const formattedLocale = removeEnFromLocaleString(item.attributes.locale);
        return {
          id: item.id,
          link: `/${formattedLocale}/teaching-resource-collection/${item.attributes.link}`,
          country: formattedLocale,
          name: collection.value?.attributes.title || '',
        };
      })
    : undefined;
});

const { findOne } = useStrapi<Collection>('collections');

const resourceParams = computed<PublicV1FiltersRequest>(() => ({
  country: locale.value,
  category: slug,
  perPage: 24,
  root: root.value,
  resource_types: resourceTypes,
  year_levels: yearLevels,
}));
const availableFilters = [
  'types',
  'learning_areas',
  'year_levels',
  'resource_types',
  'availability',
  'file_formats',
  'publishers',
];

const parent = computed(() => ({
  link: '/' + i18n.locale + '/teaching-resource-collections/',
  name: 'Teaching Resource Collections',
}));
const { user } = useUser();

const title = computed(() =>
  collection.value
    ? shortCategoryTitle(
        collection.value.attributes.title,
        locale.value,
        resourceTypes[0] || undefined,
        yearLevels[0] || undefined
      )
    : null
);

const faqContent = computed(() => collection.value?.attributes.faqs || []);
const summary = computed(() =>
  collection.value?.attributes.summary
    ? marked.parse(collection.value?.attributes.summary, { mangle: false, headerIds: false })
    : null
);
const isLoading = ref(false);

const collection = useAsync(async () => {
  if (process.browser) {
    isLoading.value = true;
  }
  try {
    const { data } = await findOne(slug);
    return data;
  } catch (err) {
    console.error(err);
    // No page matching this route
    try {
      await $handleRedirect($route.value.path);
    } catch (err) {
      error({ statusCode: 404, message: err instanceof Error ? err.message : String(err) });
    }
  } finally {
    if (process.browser) {
      isLoading.value = false;
    }
  }
  return null;
}, `category-${slug}-${locale.value}`);

const collectionBreadcrumbs = computed(() => [
  {
    link: '/' + i18n.locale + '/teaching-resource-collections/',
    name: 'Resource Collection',
  },
  {
    name: collection.value?.attributes.title ?? '',
  },
]);

const normalisedBreadCrumbs = computed<BreadCrumb[]>(
  () => breadcrumbs(collectionBreadcrumbs.value, parent.value) ?? []
);

const stripResourceTypeOutOfUrl = (url: string): void => {
  const regexGetGroups = /teaching-resource-collection\/(.*)\/(.*)\/(.*)/;
  if (regexGetGroups.exec(url)?.[2] === undefined) {
    return;
  }

  $router.push(url.replace(regexGetGroups, 'teaching-resource-collection/$1/$3'));
};

const removeEnFromLocaleString = (locale: string): string => {
  return locale.substring(3).toLowerCase();
};

watch($route, to => {
  if (!to.query.page && Object.keys(to.query).length !== 0) {
    stripResourceTypeOutOfUrl(to.fullPath);
  }
});

useMeta(() => ({
  title: title.value || undefined,
  meta: collection.value?.attributes.seo
    ? getMetaFromStrapiSEO(collection.value, $route.value.fullPath)
    : undefined,
  link: collection.value
    ? $hrefLangsResource(
        localizations.value ?? [],
        `/${i18n.locale}/teaching-resource-collection/${collection.value.attributes.link}`,
        i18n.locale,
        $route.value,
        resourceTypes[0] || yearLevels[0]
      )
    : undefined,
}));
expose({
  faqContent,
  title,
});

return { __sfc: true,$router, $route, i18n, error, $config, $hrefLangsResource, $handleRedirect, slug, yearLevels, resourceTypes, resourceListEl, blogs, resources, locale, root, localizations, findOne, resourceParams, availableFilters, parent, user, title, faqContent, summary, isLoading, collection, collectionBreadcrumbs, normalisedBreadCrumbs, stripResourceTypeOutOfUrl, removeEnFromLocaleString, CollectionHero, PinnedResources, TBreadcrumbs, ResourcesList, FaqSection, SignupCta, CountrySwitcher, PinnedBlogs }
}

})